import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { LangSwitcher } from "../components/LangSwitcher";

export default function AcceptInvite() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invites/${token}`).then(({ data }) => setInvite(data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/invites/accept", {
        token, password, language: i18n.language, consent_accepted: consent,
      });
      const u = await refresh();
      toast.success(t("auth.welcome"));
      navigate(u && u.role !== "ambassador" ? "/staff" : "/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="vy-hero px-6 py-6">
        <div className="mx-auto flex max-w-[520px] items-center">
          <span className="font-display text-xl font-bold text-white">
            Vytali<span className="text-accent">Days</span>
          </span>
          <span className="ml-auto"><LangSwitcher dark /></span>
        </div>
      </div>
      <div className="mx-auto max-w-[520px] p-4">
        {!invite && !error && <p className="p-6 text-sm text-muted-foreground">{t("common.loading")}</p>}
        {error && !invite && (
          <p data-testid="invite-error" className="vy-card p-6 text-sm font-medium text-destructive">{error}</p>
        )}
        {invite && (
          <form onSubmit={submit} data-testid="invite-form" className="vy-card p-6">
            <h1 className="font-display text-2xl font-bold">{t("auth.acceptTitle")}</h1>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="invite-email">
              {invite.name} · {invite.email}
            </p>
            <div className="mt-6">
              <label className="text-sm font-semibold">{t("auth.choosePassword")}</label>
              <Input data-testid="invite-password-input" type="password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" />
              <p className="mt-1 text-xs text-muted-foreground">{t("auth.passwordHint")}</p>
            </div>
            <label className="mt-5 flex gap-3 rounded-xl bg-muted p-4 text-sm">
              <Checkbox data-testid="invite-consent-checkbox" checked={consent}
                onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span>{t("auth.consent")}</span>
            </label>
            {error && <p data-testid="invite-submit-error" className="mt-4 text-sm font-medium text-destructive">{error}</p>}
            <Button data-testid="invite-submit-btn" type="submit" disabled={busy || !consent}
              className="mt-6 h-14 w-full rounded-full text-base font-bold">
              {busy ? t("common.loading") : t("auth.activate")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
